import React, { createContext, useContext, useEffect, useState } from "react";
import Category, { CategoryResponseData } from "../../core/models/Category";
import NetworkingServiceError from "../../core/services/network/NetworkServiceError";
import appConfig from "../../util/appConfig";

// Define the error type for Category operations
export type CategoryError = NetworkingServiceError;

// Define the shape of the context data
export type CategoriesDataType = {
  categoriesResponseData?: CategoryResponseData;
  setCategoriesResponseData: (responseData?: CategoryResponseData) => void;
  selectedCategory?: Category;
  setSelectedCategory: (category?: Category) => void;
  categoriesLoading: boolean;
  setCategoriesLoading: (isLoading: boolean) => void;
  categoriesError: CategoryError | undefined;
  setCategoriesError: (error?: CategoryError) => void;
};

// Create the context with an initial undefined value
export const CategoriesDataContext = createContext<
  CategoriesDataType | undefined
>(undefined);

// Provider component to manage and provide the context value
export const CategoriesDataProvider = ({ children }: any) => {
  const [selectedCategory, setSelectedCategory] = useState<Category>();
  const [categoriesResponseData, setCategoriesResponseData] =
    useState<CategoryResponseData>();
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [categoriesError, setCategoriesError] = useState<CategoryError>();

  // Log selectedCategory changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugCategories && selectedCategory) {
      console.debug("[Categories][SelectedCategory]", selectedCategory);
    }
  }, [selectedCategory]);

  // Log categoriesResponseData changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugCategories && categoriesResponseData) {
      console.debug(
        "[Categories][CategoriesResponseData]",
        categoriesResponseData
      );
    }
  }, [categoriesResponseData]);

  // Log categoriesLoading changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugCategories && categoriesLoading) {
      console.debug("[Categories][CategoriesLoading]", categoriesLoading);
    }
  }, [categoriesLoading]);

  // Log categoriesError changes if debugging is enabled
  useEffect(() => {
    if (appConfig.debugCategories && categoriesError) {
      console.warn(
        "[Categories][Error]",
        categoriesError?.userMessage(),
        categoriesError?.debugMessage()
      );
    }
  }, [categoriesError]);

  // Context value to be provided to consumers
  const value = {
    categoriesResponseData,
    setCategoriesResponseData,
    selectedCategory,
    setSelectedCategory,
    categoriesLoading,
    setCategoriesLoading,
    categoriesError,
    setCategoriesError,
  };

  return (
    <CategoriesDataContext.Provider value={value}>
      {children}
    </CategoriesDataContext.Provider>
  );
};

// Custom hook to use the CategoriesDataContext
export const useCategoriesContext = () => {
  const context = useContext(CategoriesDataContext);
  if (context === undefined) {
    throw new Error(
      "useCategoriesContext must be used within a CategoriesDataProvider"
    );
  }
  return context;
};
